import React, { useMemo, useRef, useState } from 'react';
import Button from '@components/atoms/button/button';
import useHotfiltersToDisplay from '@hooks/filters/use-hotfilters-to-display';
import CatalogHotfilterItem from '@components/catalog/catalog-hotfilters/catalog-hotfilter-item/catalog-hotfilter-item';
import { hotfilterGroupConfigurations, HotFilterTypes } from '@config/catalog/hotfilter-group-configurations';
import CatalogBooleanHotfilter
  from '@components/catalog/catalog-hotfilters/catalog-boolean-hotfilter/catalog-boolean-hotfilter';
import CatalogHotfilterSortby
  from '@components/catalog/catalog-hotfilters/catalog-hotfilter-item/catalog-hotfilter-sortby';
import { CatalogCategoryHotfilterProvider } from '@context/catalog-category-hotfilter.context';
import { useTranslation } from 'next-i18next';
import CatalogHotfiltersScrollWrapper
  from '@components/catalog/catalog-hotfilters/catalog-hotfilters-scroll-wrapper/catalog-hotfilters-scroll-wrapper';
import styles from './catalog-hotfilters.module.scss';

const NR_OF_FILTERS_TO_DISPLAY = 8;

const CatalogHotfilters = ({ scrollRef }: { scrollRef: React.MutableRefObject<HTMLDivElement> }) => {
  const { filterGroupsToDisplay: allFilterGroups } = useHotfiltersToDisplay(hotfilterGroupConfigurations);
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const isLayoutChangeRef = useRef(false);
  const filterGroupsToDisplay = useMemo(
    () => (showAllFilters ? allFilterGroups : allFilterGroups.slice(0, NR_OF_FILTERS_TO_DISPLAY)),
    [allFilterGroups, showAllFilters],
  );

  const handleExpandCollapseClick = () => {
    isLayoutChangeRef.current = true;
    setShowAllFilters((prev) => !prev);
    // this ref is used to ensure that the hotfilter row doesn't disappear when the user hits expand/collapse button
    setTimeout(() => {
      isLayoutChangeRef.current = false;
    }, 300);
  };

  return (
    <CatalogHotfiltersScrollWrapper
      scrollRef={scrollRef}
      isFilterOpen={isFilterOpen}
      isLayoutChangeRef={isLayoutChangeRef}
    >
      {filterGroupsToDisplay.length > 0 && <CatalogHotfilterSortby setIsFilterOpen={setIsFilterOpen} />}
      {filterGroupsToDisplay.map((filterGroup) => {
        if (filterGroup.type === HotFilterTypes.BOOLEAN) {
          return (
            <CatalogBooleanHotfilter
              filterGroup={filterGroup}
              key={`catalog-hotfilter-item-${filterGroup.label}`}
            />
          );
        }
        if (filterGroup.type === HotFilterTypes.CATEGORY) {
          return (
            <CatalogCategoryHotfilterProvider key={`catalog-hotfilter-item-${filterGroup.label}`}>
              <CatalogHotfilterItem
                filterGroup={filterGroup}
                setIsFilterOpen={setIsFilterOpen}
              />
            </CatalogCategoryHotfilterProvider>
          );
        }
        return (
          <CatalogHotfilterItem
            filterGroup={filterGroup}
            key={`catalog-hotfilter-item-${filterGroup.label}`}
            setIsFilterOpen={setIsFilterOpen}
          />
        );
      })}
      {allFilterGroups.length > NR_OF_FILTERS_TO_DISPLAY && (
        <Button
          disableDefaultStyling
          onClick={handleExpandCollapseClick}
          className={styles.catalogHotfilters__expandCollapseButton}
        >
          <span className={styles.catalogHotfilters__expandCollapseButton__text}>
            {showAllFilters ? t('CATALOG.FILTERS.LESS_HOTFILTERS') : t('CATALOG.FILTERS.ALL_HOTFILTERS')}
          </span>
          {showAllFilters ? '—' : `+`}
        </Button>
      )}
    </CatalogHotfiltersScrollWrapper>
  );
};

export default CatalogHotfilters;
